import { UserPermissions, PermissionEnum } from '@creditsights/api-ui-web';

export enum FeaturePermissionEnum {
  DOCKETS = 'ui:dockets',
  FINANCIALS = 'ui:financials',
  DOCKET_ALERTS = 'ui:dockets:alerts',
}

export const hasUIPermission = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions &&
  (userPermissions.permissions.includes(PermissionEnum.RESEARCH) ||
    userPermissions.permissions.includes(PermissionEnum.NEWS) ||
    userPermissions.permissions.includes(PermissionEnum.COVENANTS) ||
    userPermissions.permissions.includes(PermissionEnum.RISK_PRODUCTS));

export const hasPermissionToPrint = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(PermissionEnum.PRINT);
export const hasPermissionToDownload = ({ permissions }: UserPermissions): boolean =>
  permissions.includes(PermissionEnum.DOWNLOAD_CONTENT);
export const hasPermissionToShare = ({ permissions }: UserPermissions): boolean =>
  permissions.includes(PermissionEnum.SHARE_CONTENT);
export const hasPermissionToAskAnAnalyst = ({ permissions }: UserPermissions): boolean =>
  permissions.includes(PermissionEnum.ASK_AN_ANALYST);
export const hasPermissionToCopyPaste = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(PermissionEnum.COPY_PASTE);
export const hasPermissionToDockets = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(FeaturePermissionEnum.DOCKETS);
export const hasPermissionToViewDocketDocument = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes('dockets:view_document');
export const hasPermissionToRequestDocketDocument = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes('dockets:request_document');
export const hasNoPermissionToDocketDocument = (userPermissions?: UserPermissions): boolean =>
  !hasPermissionToViewDocketDocument(userPermissions) && !hasPermissionToRequestDocketDocument(userPermissions);
export const hasNoPermissionToFinancials = (userPermissions?: UserPermissions): boolean =>
  !hasPermissionToFinancials(userPermissions);
export const hasPermissionToFinancials = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(FeaturePermissionEnum.FINANCIALS);
export const hasPermissionToFinancialsExcelDownload = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes('ui:financials:download:excel');
export const hasPermissionToExport = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3);
export const hasLevel2Permissions = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2);
export const hasLevel3Permissions = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3);
export const hasPermissionToExportAllFields = (userPermissions?: UserPermissions): boolean => {
  return !!userPermissions && userPermissions.permissions.includes(PermissionEnum.UI_ADMIN_USER_ADMIN);
};
export const hasResearchPermissions = (userPermissions?: UserPermissions): boolean => {
  return !!userPermissions && userPermissions.permissions.includes(PermissionEnum.RESEARCH);
};
export const hasCovenantsPermissions = (userPermissions?: UserPermissions): boolean => {
  return !!userPermissions && userPermissions.permissions.includes(PermissionEnum.COVENANTS);
};
export const hasNewsPermissions = (userPermissions?: UserPermissions): boolean => {
  return !!userPermissions && userPermissions.permissions.includes(PermissionEnum.NEWS);
};
export const hasPermissionToCoreScores = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes('ui:core_score');

// TODO remove '&& userPermissions.hasAccessToNotProdReady()' once RP is prod ready
export const hasPermissionToRp = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.hasAccessToRP();

export const hasShareCopyWatchlist = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions?.permissions.includes('ui:share_copy_watchlists');

export const hasFinancialsKeyMetrics = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions?.permissions.includes('ui:financials:key_metrics');

export const hasAccessToRecentlyViewed = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions?.includes(PermissionEnum.PERSONALIZATION);

export const hasPermissionToDocketAlerts = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes(FeaturePermissionEnum.DOCKET_ALERTS);

export const getCustomDealPermissions = (userPermissions?: UserPermissions): Array<string> => {
  if (userPermissions) {
    return userPermissions.permissions.filter((permission) => permission.includes(':custom_deal:'));
  }
  return [];
};

export const hasRPCalculatorPermission = (userPermissions?: UserPermissions): boolean =>
  !!userPermissions && userPermissions.permissions.includes('risk_products:risk_calculator');
